import type {
  MedicalAidPerson,
  MedicalAidValidationResponse,
  PersonContact,
} from '@quromedical/models';
import {
  Alert,
  Button,
  Section,
  Skeleton,
  SkeletonProvider,
  Snackbar,
  Visible,
} from 'design-system';
import type { FormikErrors } from 'formik';
import { type ErrorMessageType, getAgeInYears, getErrorMessage } from 'helpers';
import {
  getPlaceAddressPartialFetcher,
  googleSearchAddressFetcher,
} from 'integration';
import type React from 'react';
import { useCallback, useState } from 'react';
import { MedicalAidMembershipCard, type Search } from 'screens/patient';
import { strings } from 'strings';
import { trpcClient } from 'trpc';

import { ContactForm } from '../forms';
import { InactiveMembersCard } from './InActiveMembersCard';
import { PatientSelectionCard } from './PatientSelectionCard';

type PatientStepData = {
  search: Search;
  results: MedicalAidValidationResponse;
  onSubmit: (patient: MedicalAidPerson) => void;
};

const isActiveMember = (
  medicalAidMember: Partial<MedicalAidPerson>,
): boolean => {
  const isActive = medicalAidMember.medicalAidMembership?.isActive;

  return !!isActive;
};

export const isAdult = (
  medicalAidMember: Partial<MedicalAidPerson>,
): boolean => {
  const isNotChild =
    medicalAidMember.medicalAidMembership?.isEligibleAsNextOfKin;
  const age = getAgeInYears(medicalAidMember.general?.birthDate);

  if (!age) {
    return !!isNotChild;
  }

  return age >= 18;
};

const isInactiveMember = (
  medicalAidMember: Partial<MedicalAidPerson>,
): boolean => !isActiveMember(medicalAidMember);

export const PatientStep: React.FC<PatientStepData> = ({
  results,
  search,
  onSubmit,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [selectionError, setSelectionError] = useState(false);
  const [contactError, setContactError] = useState(false);
  const [selected, setSelected] = useState<Partial<MedicalAidPerson>>();
  const [contact, setContact] = useState<Partial<PersonContact>>();
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [showAlertMessage, setShowAlertMessage] = useState<ErrorMessageType>();
  const [loading, setLoading] = useState<boolean>(false);

  const onAlertClose = useCallback(() => {
    setShowAlert(false);
  }, []);

  const onSelectionChange = useCallback(
    async (data: Partial<MedicalAidPerson>, err: boolean) => {
      try {
        setLoading(true);
        const results = await trpcClient.patient.getMedicalAidPatient.query({
          id: data.general?.identifierValue as string,
        });
        setSelected({ ...data, contact: results.contact });
        setLoading(false);
        setSelectionError(err);
      } catch (error) {
        setLoading(false);
        setShowAlert(true);
        setShowAlertMessage(getErrorMessage(error));
      }
    },
    [],
  );

  const onContactChange = useCallback(
    (
      data: Partial<PersonContact>,
      err: FormikErrors<Partial<PersonContact>>,
    ) => {
      setContact(data);
      const hasErrors = !!Object.keys(err).length;
      setContactError(hasErrors);
    },
    [],
  );

  const hasError = contactError || selectionError;
  const showErrors = submitted && hasError;

  const handleSubmit = useCallback(() => {
    setSubmitted(true);

    if (!hasError) {
      // since we have validated we can safely assume the data type
      const result = { ...selected, contact } as MedicalAidPerson;
      onSubmit(result);
    }
  }, [contact, hasError, onSubmit, selected]);

  // All members should have the same medical aid membership in this context
  const medicalAid = results[0]?.medicalAidMembership?.medicalAid;

  const activeMembers = results.filter(
    (result) => isAdult(result) && isActiveMember(result),
  );
  const inactiveMembers = results.filter(isInactiveMember);

  return (
    <SkeletonProvider loading={loading}>
      <Section>
        <MedicalAidMembershipCard
          membershipNumber={medicalAid?.membershipNumber}
          plan={medicalAid?.plan}
          schemeName={medicalAid?.schemeName}
        />
      </Section>

      <Visible if={inactiveMembers.length > 0}>
        <Section>
          <InactiveMembersCard inactiveMembers={inactiveMembers} />
        </Section>
      </Visible>

      <Visible if={activeMembers.length > 0}>
        <Section>
          <PatientSelectionCard
            value={selected}
            results={activeMembers}
            initialIdNumber={search.id}
            onChange={onSelectionChange}
            showErrors={showErrors}
          />
        </Section>
        <Section>
          <Skeleton>
            <ContactForm
              key={`${JSON.stringify(selected?.contact)}`}
              onChange={onContactChange}
              showErrors={showErrors}
              addressFetcher={googleSearchAddressFetcher}
              getAddressPartial={getPlaceAddressPartialFetcher}
              initial={selected?.contact || {}}
            />
          </Skeleton>
        </Section>
        <Section alignItems="flex-end" hasBottomMargin>
          <Button onPress={handleSubmit} text={strings.ButtonTextNext} />
        </Section>
      </Visible>

      <Snackbar isOpen={showAlert} onClose={onAlertClose}>
        <Alert
          actionIcon="close"
          backgroundColor="status-critical"
          onAction={onAlertClose}
          title={strings.AlertError}
          body={(showAlertMessage as string) || strings.GenericErrorMessage}
          icon="error"
        />
      </Snackbar>
    </SkeletonProvider>
  );
};
